/** The command dialog: every consequential action confirmed in one place (UI spec §4, UX03, V08-A03, V08-A06). */
import type { Admitted, CommandOutcome, CommandRequest } from "../contracts/commands.ts";
import type { Environment, Mode } from "../contracts/envelope.ts";
import { ApiRequestError } from "./client.ts";
import {
  INTENTS,
  brokerNotes,
  buildRequest,
  amountProblem,
  controlName,
  cooldownProblem,
  cooldownText,
  freesIdentity,
  instantFromMicros,
  isMoving,
  isSettled,
  modificationProblem,
  outOfBandProblem,
  outcomeNotice,
  paiseOnWire,
  parsePaise,
  quantityProblem,
  reasonProblem,
  reviewProblem,
  stageOf,
  type CommandIntent,
  type RoutingGuard,
  type Stage,
} from "./command-dialog.ts";
import type { CommandServices, Proof } from "./command-port.ts";
import { Scope, h, setChildren } from "./dom.ts";
import { formatInstant } from "./format.ts";
import type { IntentKeys } from "./intent-keys.ts";
import { secondsLeft, serverNowMicros, type Mono, type ServerClock } from "./time.ts";

/** What a view knows about the thing it asks to act on. */
export interface IntentTarget {
  readonly intent: CommandIntent;
  readonly accountId: string;
  readonly environment: Environment;
  readonly mode: Mode;
  /** The scope in words: an account, an order, a position. */
  readonly scope: string;
  /** The current quantities in words. */
  readonly quantities: string;
  /** The intent's own scope on the wire (an order id, an instrument). */
  readonly payload: Readonly<Record<string, string>>;
  /** The account state version the command expects. */
  readonly expectedVersion: number;
  /** The most that can be reduced, for an intent that asks for a quantity. */
  readonly maxQuantity: number;
  readonly clock: ServerClock;
  /** The P0-E guards from the server's routing facts (INC-0807); absent: the INC-0609 dialog. */
  readonly routing?: RoutingGuard;
}

export interface DialogDeps {
  readonly services: CommandServices;
  readonly mono: Mono;
  /** Called when the server says the state moved on: the view re-reads its snapshot. */
  readonly onConflict: () => void;
  /** Called when a command settled, so the view can re-read what it changed. */
  readonly onSettled: () => void;
  /** Called after a code was issued, so the view re-reads the routing facts (the new code replaces the old). */
  readonly onIssued?: () => void;
}

export interface DialogHandle {
  readonly element: HTMLElement;
  close(): void;
}

/** How long a request stays valid after the server's clock now (the server caps it at 15 minutes). */
const EXPIRY_MICROS = 300_000_000n;
const STEP_UP_REFUSALS = ["STEP_UP_STALE", "NO_FRESH_FACTOR"];

/** Integer paise as exact rupees text for a field (no float). */
function rupeesText(paise: number): string {
  const total = BigInt(paise);
  return `${(total / 100n).toString()}.${(total % 100n).toString().padStart(2, "0")}`;
}

/** The stable id of an intent: the same action on the same thing at the same version. */
export function intentIdOf(target: IntentTarget): string {
  const scope = Object.entries(target.payload)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`);
  return [target.intent, target.accountId, ...scope, `v${String(target.expectedVersion)}`].join(
    "|",
  );
}

type Shown =
  | { readonly kind: "form" }
  | { readonly kind: "working"; readonly text: string; readonly stage: Stage }
  | { readonly kind: "step-up-required" }
  | { readonly kind: "unreachable" }
  | { readonly kind: "conflict"; readonly message: string }
  | { readonly kind: "refused"; readonly message: string }
  | {
      readonly kind: "outcome";
      readonly outcome: CommandOutcome;
      readonly replayed: boolean;
      readonly reasons: readonly string[];
    };

const STEPS: readonly { readonly label: string; readonly stages: readonly Stage[] }[] = [
  { label: "Requested", stages: ["requested"] },
  { label: "Accepted or rejected", stages: ["awaiting-approval", "accepted", "rejected"] },
  { label: "Applying", stages: ["applying"] },
  {
    label: "Applied, failed or unknown",
    stages: ["applied", "failed", "unknown", "expired", "cancelled"],
  },
];

function kindsOf(error: ApiRequestError): string[] {
  const kind = error.body?.kind ?? null;
  return kind === null ? [] : typeof kind === "string" ? [kind] : [...kind];
}

/** Why a code could not be asked for, in words; the blockers of the issue route (403, 429). */
function codeRefusal(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return "No answer arrived, so it is not known whether a code was sent. Nothing was applied.";
  }
  const kinds = kindsOf(error);
  if (error.status === 403 && kinds.includes("CHANNEL_UNBOUND")) {
    return "No channel is bound to your identity, so no confirmation code can be sent. Ask an administrator to bind one. Nothing was sent.";
  }
  if (error.status === 429) {
    const wait =
      error.retryAfterSeconds === null ? "" : ` Try again in ${String(error.retryAfterSeconds)} s.`;
    return `Too many confirmation codes were asked for (at most three an hour).${wait} Nothing was sent.`;
  }
  const why = kinds.length === 0 ? "" : ` (${kinds.join(", ")})`;
  return `The server will not send a confirmation code${why}: status ${String(error.status)}. Nothing was sent.`;
}

function shownFromError(error: unknown, expected: number): Shown {
  if (!(error instanceof ApiRequestError)) {
    return { kind: "unreachable" };
  }
  const kinds = kindsOf(error);
  if (error.status === 403 && kinds.some((kind) => STEP_UP_REFUSALS.includes(kind))) {
    return { kind: "step-up-required" };
  }
  if (error.status === 409 && kinds.includes("IDEMPOTENCY_CONFLICT")) {
    return {
      kind: "refused",
      message:
        "This request identity was already used for a different request. Nothing new was applied; start a new request.",
    };
  }
  if (error.status === 409) {
    return {
      kind: "conflict",
      message: `The account changed after this request was prepared (it expected version ${String(expected)}). Nothing was applied. The view now shows the new state: review it and start a new request.`,
    };
  }
  const why = kinds.length === 0 ? "" : ` (${kinds.join(", ")})`;
  const wait =
    error.retryAfterSeconds === null ? "" : ` Try again in ${String(error.retryAfterSeconds)} s.`;
  return {
    kind: "refused",
    message: `The server refused the request${why}: status ${String(error.status)}. Nothing was applied.${wait}`,
  };
}

/** The outcome of a command as three separate displays: its lifecycle, its effects, the orders' own. */
export function renderOutcome(
  outcome: CommandOutcome,
  replayed = false,
  reasons: readonly string[] = [],
): HTMLElement {
  const notes = brokerNotes(outcome);
  const unresolved =
    outcome.unknown || outcome.state === "FAILED" || notes.some((note) => note.unresolved);
  return h(
    "div",
    { class: "outcome", attrs: { "data-command-state": outcome.state } },
    h("h3", {}, `Command ${outcome.commandId.slice(0, 8)}: ${stageOf(outcome.state)}`),
    replayed
      ? h(
          "p",
          { class: "replayed" },
          "This repeats a request already made under the same identity; it is the same command, not a second one.",
        )
      : null,
    reasons.length === 0 ? null : h("p", {}, `Reasons: ${reasons.join(", ")}.`),
    h("p", { class: "notice" }, outcomeNotice(outcome)),
    h(
      "p",
      {},
      "Received ",
      formatInstant(outcome.receivedAt),
      outcome.decidedAt === null ? "" : `; decided ${formatInstant(outcome.decidedAt)}`,
      outcome.appliedAt === null ? "" : `; applied ${formatInstant(outcome.appliedAt)}`,
      ".",
    ),
    h("h4", {}, "Application effects (journaled by the controller)"),
    outcome.applicationEffect.length === 0
      ? h("p", {}, "None recorded yet.")
      : h(
          "ul",
          {},
          ...outcome.applicationEffect.map((effect) =>
            h("li", {}, `${effect.kind} ${effect.eventId.slice(0, 8)}`),
          ),
        ),
    h("h4", {}, "Broker and execution lifecycle (separate from the command)"),
    notes.length === 0
      ? h("p", {}, "No orders reported for this command. A successful request is not a fill.")
      : h(
          "ul",
          { class: "broker-states" },
          ...notes.map((note) =>
            h(
              "li",
              {
                attrs: {
                  "data-broker-state": note.brokerState,
                  "data-unresolved": String(note.unresolved),
                },
              },
              `Order ${note.orderId}: ${note.brokerState} - ${note.text}.`,
            ),
          ),
        ),
    unresolved
      ? h(
          "p",
          { class: "unresolved", attrs: { role: "alert" } },
          `Exposure may be unresolved (retry guidance: ${outcome.retryGuidance}). Do not treat this as flat or closed.`,
        )
      : null,
  );
}

function focusable(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      "button, textarea, input, select, a[href], [tabindex='0']",
    ),
  ].filter((element) => !element.hasAttribute("disabled"));
}

interface Chrome {
  readonly backdrop: HTMLElement;
  readonly dialog: HTMLElement;
  readonly scope: Scope;
  readonly close: () => void;
}

/** The dialog frame: modal semantics, a focus trap, Escape, and focus returned to the opener. */
function createChrome(title: string, titleId: string, opener: HTMLElement | null): Chrome {
  const scope = new Scope();
  const dialog = h("div", {
    class: "dialog",
    attrs: { role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, tabindex: "-1" },
  });
  const backdrop = h("div", { class: "dialog-backdrop", attrs: { "data-dialog": title } }, dialog);
  let closed = false;
  const close = (): void => {
    if (closed) {
      return;
    }
    closed = true;
    scope.dispose();
    backdrop.remove();
    if (opener?.isConnected === true) {
      opener.focus();
    }
  };
  // Escape still closes when focus has fallen out of the dialog to the page body (INC-0612 F2):
  // the listener lives on the document until the dialog is disposed.
  scope.listen(backdrop.ownerDocument, "keydown", (event) => {
    const key = event as KeyboardEvent;
    if (key.key === "Escape" && key.target === backdrop.ownerDocument.body) {
      key.preventDefault();
      close();
    }
  });
  scope.listen(backdrop, "keydown", (event) => {
    const key = event as KeyboardEvent;
    if (key.key === "Escape") {
      key.preventDefault();
      close();
      return;
    }
    if (key.key !== "Tab") {
      return;
    }
    const items = focusable(dialog);
    const at = items.indexOf(dialog.ownerDocument.activeElement as HTMLElement);
    const step = key.shiftKey ? -1 : 1;
    const next =
      at === -1 ? (key.shiftKey ? items.length - 1 : 0) : (at + step + items.length) % items.length;
    key.preventDefault();
    items.forEach((item, index) => {
      if (index === next) {
        item.focus();
      }
    });
  });
  return { backdrop, dialog, scope, close };
}

function progression(shown: Shown): HTMLElement {
  const current: Stage | null =
    shown.kind === "outcome"
      ? stageOf(shown.outcome.state)
      : shown.kind === "working"
        ? shown.stage
        : null;
  return h(
    "ol",
    { class: "progression", attrs: { "aria-label": "Command progression" } },
    ...STEPS.map((step) => {
      const here = current !== null && step.stages.includes(current);
      return h(
        "li",
        { attrs: { "data-step": step.label, ...(here ? { "aria-current": "step" } : {}) } },
        here ? `${step.label} (now: ${current})` : step.label,
      );
    }),
  );
}

/** Open the dialog for an intent (appended to the body); the confirm button is the only way to send it. */
export function openCommandDialog(
  deps: DialogDeps,
  target: IntentTarget,
  opener: HTMLElement | null,
): DialogHandle {
  const { services } = deps;
  const spec = INTENTS[target.intent];
  const intentId = intentIdOf(target);
  const titleId = `dialog-title-${intentId.replaceAll(/[^A-Za-z0-9]/g, "-")}`;
  const chrome = createChrome(spec.label, titleId, opener);
  const record = services.keys.open(intentId);
  const sent = record.request;
  const name = controlName(target.intent, target.accountId, target.scope);
  let busy = false;
  let proof: Proof | null = null;
  let shown: Shown = { kind: "form" };
  let timer: ReturnType<typeof setTimeout> | undefined;
  chrome.scope.add(() => {
    clearTimeout(timer);
  });

  const reason = h("textarea", {
    attrs: {
      id: `${titleId}-reason`,
      rows: "3",
      required: "",
      "aria-describedby": `${titleId}-problem`,
    },
  });
  const quantity = h("input", {
    attrs: { id: `${titleId}-quantity`, type: "text", inputmode: "numeric", autocomplete: "off" },
  });
  const guard = target.routing ?? null;
  const amount = h("input", {
    attrs: { id: `${titleId}-amount`, type: "text", inputmode: "decimal", autocomplete: "off" },
  });
  const review = h("textarea", {
    attrs: { id: `${titleId}-review`, rows: "3", "aria-describedby": `${titleId}-problem` },
  });
  // The code is typed by hand and lives only in this field: never in storage, a URL or the stored body (U049).
  const oob = h("input", {
    attrs: {
      id: `${titleId}-oob`,
      type: "text",
      inputmode: "numeric",
      autocomplete: "off",
      spellcheck: "false",
    },
  });
  // Closing the dialog empties the code (review F2): it is never kept past the dialog that took it.
  chrome.scope.add(() => {
    oob.value = "";
  });
  const cooldownLine = h("p", { class: "cooldown", attrs: { "data-cooldown": "", role: "timer" } });
  const problem = h("p", {
    class: "problem",
    attrs: { id: `${titleId}-problem`, role: "alert" },
  });
  const confirm = h(
    "button",
    {
      class: "confirm",
      attrs: { type: "submit", "aria-label": `Confirm ${name}`, "data-confirm": target.intent },
    },
    `Confirm: ${spec.label}`,
  );
  const close = h(
    "button",
    {
      attrs: { type: "button", "data-close": "" },
      on: {
        click: () => {
          chrome.close();
        },
      },
    },
    "Close",
  );
  const progress = h("section", {
    class: "progress",
    attrs: { "aria-live": "polite", "aria-label": "Progress", tabindex: "-1" },
  });
  const checkAgain = h("button", { attrs: { type: "button", "data-check": "" } }, "Check status");
  const stepUpNote = spec.stepUp
    ? h(
        "p",
        { class: "step-up" },
        "This action needs a fresh second factor. Sign in with your second factor again if asked; the request keeps its identity.",
      )
    : null;

  if (sent !== null) {
    reason.value = sent.reason;
    const sentQuantity = sent.payload.quantity;
    quantity.value = typeof sentQuantity === "number" ? String(sentQuantity) : "";
    const paise = spec.amount === null ? undefined : sent.payload[spec.amount.key];
    amount.value = typeof paise === "number" ? rupeesText(paise) : "";
    const written = sent.payload.review;
    review.value = typeof written === "object" && written !== null ? written.text : "";
  }
  const countdown =
    guard?.cooldown === undefined ? null : { endsAt: guard.cooldown.endsAt, clock: guard.clock };
  if (countdown !== null) {
    const tick = (): void => {
      const left = secondsLeft(countdown.endsAt, countdown.clock, deps.mono);
      cooldownLine.textContent =
        left === 0
          ? `The cooldown after the halt has elapsed (it ended ${formatInstant(countdown.endsAt)}).`
          : `Cooldown after the halt: ${cooldownText(left)} remain (until ${formatInstant(countdown.endsAt)}). A resume is refused until then.`;
    };
    tick();
    chrome.scope.every(1000, tick);
  }
  // A code is needed when the guard carries a live record or says one is required (RISK_INCREASING).
  const needsCode = guard?.outOfBand !== undefined || guard?.outOfBandRequired === true;
  let issued: { readonly channel: string; readonly expiresAt: string } | undefined;
  let codeBlocker: string | null = null;
  const liveCode = (): { readonly channel: string; readonly expiresAt: string } | undefined =>
    issued ?? guard?.outOfBand;
  const codeLabel = h("label", { attrs: { for: `${titleId}-oob`, "data-code": "none" } });
  /** The code's state as an attribute (none, live, asking, issued, refused) a test waits on (review F5). */
  const codeState = (state: string): void => {
    codeLabel.setAttribute("data-code", state);
  };
  const codeButton = h(
    "button",
    { attrs: { type: "button", "data-request-code": "" } },
    "Send me a code",
  );
  const drawCode = (): void => {
    const live = liveCode();
    codeState(issued !== undefined ? "issued" : live === undefined ? "none" : "live");
    codeLabel.textContent =
      live === undefined
        ? "Out-of-band confirmation code (none sent yet: ask for one, then type it by hand)"
        : `Out-of-band confirmation code (sent by ${live.channel}, valid until ${formatInstant(live.expiresAt)}; type it by hand)`;
    codeButton.textContent = live === undefined ? "Send me a code" : "Send me a new code";
  };
  drawCode();
  const stored = (): CommandRequest | null => services.keys.find(intentId)?.request ?? null;
  const render = (): void => {
    const holding = chrome.dialog.contains(chrome.dialog.ownerDocument.activeElement);
    const lock = stored() !== null || busy || shown.kind === "outcome";
    reason.disabled = lock;
    quantity.disabled = lock;
    amount.disabled = lock;
    review.disabled = lock;
    oob.disabled = busy;
    // A stored request may still need a fresh code for its retry (same key): only an outcome ends it (review F3).
    codeButton.disabled = busy || shown.kind === "outcome";
    confirm.hidden =
      shown.kind === "outcome" || shown.kind === "conflict" || shown.kind === "refused";
    confirm.disabled = busy;
    checkAgain.hidden = !(
      shown.kind === "unreachable" ||
      (shown.kind === "outcome" && !isSettled(shown.outcome.state))
    );
    const lines: (HTMLElement | null)[] = [progression(shown)];
    if (shown.kind === "working") {
      lines.push(h("p", { class: "working" }, shown.text));
    } else if (shown.kind === "step-up-required") {
      lines.push(
        h(
          "p",
          { class: "alert", attrs: { role: "alert" } },
          "A fresh second-factor step-up is required. Complete it, then press Confirm again: the request keeps the same identity.",
        ),
      );
    } else if (shown.kind === "unreachable") {
      lines.push(
        h(
          "p",
          { class: "alert", attrs: { role: "alert" } },
          "No usable answer arrived. The request may or may not have been received. Check its status with the same identity; do not start a second request.",
        ),
      );
    } else if (shown.kind === "conflict" || shown.kind === "refused") {
      lines.push(h("p", { class: "alert", attrs: { role: "alert" } }, shown.message));
    } else if (shown.kind === "outcome") {
      lines.push(renderOutcome(shown.outcome, shown.replayed, shown.reasons));
    }
    setChildren(progress, lines);
    holdFocus(holding);
  };

  /**
   * Keep focus inside the dialog when the focused control is hidden or disabled by a state change
   * (the confirm button on submit), whether the browser has already blurred it or not: it moves to
   * the progress region, where Escape and Tab still work (UX09, INC-0612 F2).
   */
  const holdFocus = (holding: boolean): void => {
    const lost =
      chrome.dialog.querySelector(":focus") === null ||
      chrome.dialog.querySelector("[hidden] :focus, [hidden]:focus, :focus[disabled]") !== null;
    if (holding && lost) {
      progress.focus();
    }
  };

  const later = (task: () => void): void => {
    clearTimeout(timer);
    timer = setTimeout(task, services.pollMs);
  };

  const apply = (outcome: CommandOutcome, replayed: boolean, reasons: readonly string[]): void => {
    shown = { kind: "outcome", outcome, replayed, reasons };
    oob.value = "";
    if (freesIdentity(outcome.state)) {
      services.keys.forget(intentId);
    }
    if (isSettled(outcome.state)) {
      deps.onSettled();
    }
    render();
    if (isMoving(outcome.state)) {
      later(() => {
        void inspect(outcome.commandId, replayed, reasons);
      });
    }
  };

  const inspect = async (
    commandId: string,
    replayed = false,
    reasons: readonly string[] = [],
  ): Promise<void> => {
    try {
      const outcome = await services.port.outcome(commandId);
      if (outcome !== null && !chrome.scope.disposed) {
        apply(outcome, replayed, reasons);
      }
    } catch {
      if (!chrome.scope.disposed) {
        later(() => {
          void inspect(commandId, replayed, reasons);
        });
      }
    }
  };

  const send = async (): Promise<void> => {
    if (busy) {
      return;
    }
    let request = stored();
    if (request === null) {
      const checks: readonly (readonly [string | null, HTMLElement])[] = [
        [guard === null ? null : modificationProblem(guard), confirm],
        [reasonProblem(reason.value), reason],
        [spec.quantity ? quantityProblem(quantity.value, target.maxQuantity) : null, quantity],
        [
          spec.amount === null ? null : amountProblem(amount.value, guard?.amountAbovePaise),
          amount,
        ],
        [guard?.reviewRequired === true ? reviewProblem(review.value) : null, review],
      ];
      const failed = checks.find(([why]) => why !== null);
      problem.textContent = failed?.[0] ?? "";
      if (failed !== undefined) {
        failed[1].focus();
        return;
      }
      request = buildRequest({
        intent: target.intent,
        clientKey: services.keys.open(intentId).clientKey,
        accountId: target.accountId,
        environment: target.environment,
        mode: target.mode,
        reason: reason.value.trim(),
        expectedVersions: { account: target.expectedVersion },
        expiresAt: instantFromMicros(serverNowMicros(target.clock, deps.mono) + EXPIRY_MICROS),
        scope: {
          ...target.payload,
          ...(spec.quantity ? { quantity: Number(quantity.value.trim()) } : {}),
          ...(spec.amount === null
            ? {}
            : { [spec.amount.key]: paiseOnWire(parsePaise(amount.value)) }),
          ...(guard?.reviewRequired === true ? { review: { text: review.value.trim() } } : {}),
        },
      });
    }
    // The cooldown and the out-of-band code gate every send, a retry included; the server rechecks both.
    const wait = guard === null ? null : cooldownProblem(guard, deps.mono);
    const code =
      guard === null
        ? null
        : ((liveCode() === undefined ? codeBlocker : null) ??
          outOfBandProblem(oob.value, guard, deps.mono, issued));
    problem.textContent = wait ?? code ?? "";
    if (wait !== null || code !== null) {
      (wait === null ? oob : confirm).focus();
      return;
    }
    busy = true;
    try {
      if (spec.stepUp && proof === null) {
        shown = {
          kind: "working",
          text: "Asking for a single-use authorisation (step-up)...",
          stage: "requested",
        };
        render();
        const authorisation = await services.port.stepUp({
          action: request.action,
          accountId: request.accountId,
          environment: request.environment,
          payload: request.payload,
          expectedVersion: target.expectedVersion,
        });
        if (authorisation === null) {
          return;
        }
        proof = { authorisationId: authorisation.authorisationId, nonce: authorisation.nonce };
      }
      services.keys.keepRequest(intentId, request);
      shown = { kind: "working", text: "Sending the request...", stage: "requested" };
      render();
      const admitted: Admitted | null = await services.port.submit(
        request,
        proof,
        needsCode ? { code: oob.value.trim() } : null,
      );
      if (admitted === null) {
        return;
      }
      services.keys.keepCommand(intentId, admitted.commandId);
      apply(admitted, admitted.replayed, admitted.reasons);
    } catch (error) {
      shown = shownFromError(error, target.expectedVersion);
      if (shown.kind === "step-up-required") {
        // The single-use proof is spent or stale: the next confirm asks for a fresh one (review F2).
        proof = null;
      }
      if (shown.kind === "conflict") {
        services.keys.forget(intentId);
        deps.onConflict();
      }
    } finally {
      busy = false;
      render();
    }
  };

  const form = h(
    "form",
    {
      attrs: { novalidate: "" },
      on: {
        submit: (event) => {
          event.preventDefault();
          void send();
        },
      },
    },
    h("label", { attrs: { for: `${titleId}-reason` } }, "Reason (recorded with the command)"),
    reason,
    spec.quantity
      ? h(
          "label",
          { attrs: { for: `${titleId}-quantity` } },
          `Quantity to reduce (whole shares, at most ${String(target.maxQuantity)})`,
        )
      : null,
    spec.quantity ? quantity : null,
    spec.amount === null
      ? null
      : h("label", { attrs: { for: `${titleId}-amount` } }, spec.amount.label),
    spec.amount === null ? null : amount,
    guard?.reviewRequired === true
      ? h(
          "label",
          { attrs: { for: `${titleId}-review` } },
          "Written review (required before resuming; recorded with the command)",
        )
      : null,
    guard?.reviewRequired === true ? review : null,
    countdown === null ? null : cooldownLine,
    needsCode ? codeLabel : null,
    needsCode ? oob : null,
    needsCode ? codeButton : null,
    problem,
    stepUpNote,
    h("div", { class: "actions" }, confirm, checkAgain, close),
  );
  const askForCode = async (): Promise<void> => {
    if (busy) {
      return;
    }
    busy = true;
    codeBlocker = null;
    problem.textContent = "";
    render();
    codeState("asking");
    try {
      const got = await services.port.requestCode(spec.action, target.accountId);
      if (got !== null) {
        issued = { channel: got.channel, expiresAt: got.expiresAt };
        deps.onIssued?.();
      }
    } catch (error) {
      codeBlocker = codeRefusal(error);
      problem.textContent = codeBlocker;
    } finally {
      busy = false;
      drawCode();
      render();
    }
    if (codeBlocker !== null) {
      codeState("refused");
    } else {
      oob.focus();
    }
  };
  chrome.scope.listen(codeButton, "click", () => {
    void askForCode();
  });
  chrome.scope.listen(checkAgain, "click", () => {
    if (shown.kind === "outcome") {
      void inspect(shown.outcome.commandId, shown.replayed);
    } else {
      void send();
    }
  });

  setChildren(chrome.dialog, [
    h("h2", { attrs: { id: titleId } }, spec.label),
    h(
      "dl",
      { class: "definitions" },
      h("dt", {}, "Account"),
      h("dd", {}, target.accountId),
      h("dt", {}, "Environment and mode"),
      h("dd", {}, `${target.environment} / ${target.mode}`),
      h("dt", {}, "Scope"),
      h("dd", {}, target.scope),
      h("dt", {}, "Current quantities"),
      h("dd", {}, target.quantities),
      h("dt", {}, "Expected state version"),
      h("dd", {}, `v${String(target.expectedVersion)}`),
      h("dt", {}, "Request identity"),
      h("dd", {}, record.clientKey.slice(0, 8)),
      ...(guard?.modification === undefined
        ? []
        : [
            h("dt", {}, "Modifications of this order"),
            h(
              "dd",
              { attrs: { "data-modifications": "" } },
              `${String(guard.modification.used)} used of ${String(guard.modification.cap)} allowed`,
            ),
          ]),
    ),
    h("h3", {}, "Effects"),
    h("ul", { class: "effects" }, ...spec.effects.map((effect) => h("li", {}, effect))),
    form,
    progress,
  ]);
  render();
  if (record.commandId !== null) {
    shown = {
      kind: "working",
      text: "Looking up the command already sent under this identity...",
      stage: "requested",
    };
    render();
    void inspect(record.commandId);
  }
  (opener?.ownerDocument ?? document).body.append(chrome.backdrop);
  (sent === null ? reason : chrome.dialog).focus();
  return { element: chrome.backdrop, close: chrome.close };
}

/** Read-only inspection of one command by its durable id: how an unknown outcome stays inspectable (UX03). */
export function openInspector(
  deps: Pick<DialogDeps, "services">,
  commandId: string,
  opener: HTMLElement | null,
): DialogHandle {
  const titleId = `inspect-${commandId.slice(0, 8)}`;
  const chrome = createChrome("Inspect command", titleId, opener);
  const body = h("section", { attrs: { "aria-live": "polite", "aria-label": "Command outcome" } });
  const load = async (): Promise<void> => {
    try {
      const outcome = await deps.services.port.outcome(commandId);
      if (outcome !== null && !chrome.scope.disposed) {
        setChildren(body, [renderOutcome(outcome)]);
      }
    } catch {
      setChildren(body, [
        h("p", { attrs: { role: "alert" } }, "The command could not be queried. Try again."),
      ]);
    }
  };
  const again = h(
    "button",
    {
      attrs: { type: "button", "data-check": "" },
      on: {
        click: () => {
          void load();
        },
      },
    },
    "Check status",
  );
  const close = h(
    "button",
    {
      attrs: { type: "button", "data-close": "" },
      on: {
        click: () => {
          chrome.close();
        },
      },
    },
    "Close",
  );
  setChildren(chrome.dialog, [
    h("h2", { attrs: { id: titleId } }, "Inspect command"),
    body,
    h("div", { class: "actions" }, again, close),
  ]);
  (opener?.ownerDocument ?? document).body.append(chrome.backdrop);
  void load();
  close.focus();
  return { element: chrome.backdrop, close: chrome.close };
}

/** A consequential non-trading request (an export, its cancellation, a notification setting). */
export interface ConfirmIntent {
  /** The intent: the action and its exact body without the key; the same id re-uses the same key. */
  readonly intentId: string;
  /** The `data-dialog-id` of the dialog. */
  readonly id: string;
  readonly title: string;
  readonly summary: readonly (readonly [string, string])[];
  readonly confirmLabel: string;
  /** Send the request under `clientKey`; resolve with the result text, reject with an `Error` shown. */
  readonly run: (clientKey: string) => Promise<string>;
}

/**
 * Confirm a non-trading request in the command dialog's frame, with its one-key-per-intent rule:
 * the key is the intent's (`IntentKeys`: memory, session storage, other tabs), re-used by every
 * retry and refresh of the same body, and forgotten once the server has answered.
 */
export function openConfirmDialog(
  keys: IntentKeys,
  intent: ConfirmIntent,
  opener: HTMLElement,
): DialogHandle {
  const titleId = `confirm-${intent.id}`;
  const chrome = createChrome(intent.title, titleId, opener);
  const result = h("p", {
    class: "dialog-result",
    attrs: { role: "status", "aria-live": "polite" },
  });
  const confirm = h(
    "button",
    { class: "confirm", attrs: { type: "button", "data-dialog": "confirm" } },
    intent.confirmLabel,
  );
  const close = h("button", { attrs: { type: "button", "data-dialog": "cancel" } }, "Close");
  let done = false;
  chrome.scope.listen(close, "click", chrome.close);
  chrome.scope.listen(confirm, "click", () => {
    if (done) {
      chrome.close();
      return;
    }
    confirm.toggleAttribute("disabled", true);
    result.setAttribute("role", "status");
    result.textContent = "Working…";
    intent.run(keys.open(intent.intentId).clientKey).then(
      (message) => {
        done = true;
        keys.forget(intent.intentId);
        result.textContent = message;
        confirm.textContent = "Close";
        confirm.toggleAttribute("disabled", false);
      },
      (error: unknown) => {
        result.setAttribute("role", "alert");
        result.textContent = `Not done: ${error instanceof Error ? error.message : "the request failed"}. You can retry; the same request key is used.`;
        confirm.toggleAttribute("disabled", false);
      },
    );
  });
  chrome.dialog.dataset.dialogId = intent.id;
  setChildren(chrome.dialog, [
    h("h2", { attrs: { id: titleId } }, intent.title),
    h(
      "dl",
      { class: "definitions" },
      ...intent.summary.flatMap(([name, value]) => [h("dt", {}, name), h("dd", {}, value)]),
    ),
    result,
    h("div", { class: "actions" }, confirm, close),
  ]);
  opener.ownerDocument.body.append(chrome.backdrop);
  confirm.focus();
  return { element: chrome.backdrop, close: chrome.close };
}
